import type { UnifiedSelectionRectItem } from './types/unified-selection-rect-item'
import type { UnifiedSelectionRectGeometry } from './types/unified-selection-rect-geometry'

export function hasUnifiedSelectionResizeTargetChanged(
  currentWidth: number,
  currentHeight: number,
  targetWidth: number,
  targetHeight: number,
  epsilon = 1e-4
): boolean {
  return (
    Math.abs(targetWidth - currentWidth) > epsilon ||
    Math.abs(targetHeight - currentHeight) > epsilon
  )
}

export function computeUnifiedSelectionScaledResizePreview(params: {
  items: UnifiedSelectionRectItem[]
  targetWidth: number
  targetHeight: number
  minDimension?: number
}): Record<string, UnifiedSelectionRectGeometry> | null {
  const { items, targetWidth, targetHeight, minDimension = 0.01 } = params

  if (items.length === 0) return null

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const item of items) {
    minX = Math.min(minX, item.x)
    minY = Math.min(minY, item.y)
    maxX = Math.max(maxX, item.x + item.width)
    maxY = Math.max(maxY, item.y + item.height)
  }

  const boxWidth = maxX - minX
  const boxHeight = maxY - minY

  if (
    !Number.isFinite(boxWidth) ||
    !Number.isFinite(boxHeight) ||
    boxWidth <= 0 ||
    boxHeight <= 0
  ) {
    return null
  }

  const nextWidth = Math.max(minDimension, targetWidth)
  const nextHeight = Math.max(minDimension, targetHeight)
  const scaleX = nextWidth / boxWidth
  const scaleY = nextHeight / boxHeight
  const updates: Record<string, UnifiedSelectionRectGeometry> = {}

  for (const item of items) {
    updates[item.id] = {
      x: minX + (item.x - minX) * scaleX,
      y: minY + (item.y - minY) * scaleY,
      width: item.width * scaleX,
      height: item.height * scaleY,
    }
  }

  return updates
}
